import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SearchableSelect } from '../SearchableSelect';
import { QBOperator } from './QueryBuilder.constants';
import useStyles from './QueryBuilder.styles';
import type {
  QBFilterOptions,
  QBFilterValueType,
  QBProps,
  QBStep
} from './QueryBuilder.types';
import {
  getFieldOptions,
  getOperatorsForField,
  getSelectedOptions,
  getUniqueBaseFields,
  isQBContainsOperator,
  isQBInOperator
} from './QueryBuilder.utils';
import { QueryBuilderValueDisplay } from './QueryBuilderValueDisplay';

const QueryBuilder = function QueryBuilder({
  config,
  filter,
  onChange,
  onDelete
}: QBProps) {
  const { field, operator, value } = filter || {};
  const { classes } = useStyles();
  const fieldInputRef = useRef<HTMLInputElement>(null);

  const baseFields = useMemo(() => getUniqueBaseFields(config), [config]);
  const [step, setStep] = useState<QBStep>(() => {
    if (field && operator && value !== null) return 'final';
    if (field && operator) return 'value';
    if (field) return 'operator';
    return 'field';
  });
  const [selectedField, setSelectedField] = useState<string | null>(
    field || null
  );
  const [selectedOperator, setSelectedOperator] = useState<string | null>(
    operator || null
  );

  const [selectedValue, setSelectedValue] = useState<string | null>(
    value || null
  );
  const selectedFieldConfig = selectedField ? config[selectedField] : undefined;

  // used to manage the selected options state for dropdowns, this is separate from the selectedValue state which is the actual value of the filter
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  // Listen for '/' key press to focus the field autocomplete when no field is set
  useEffect(() => {
    const handleSlashKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isInputFocused =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (event.key === '/' && !field && step === 'field' && !isInputFocused) {
        event.preventDefault();
        fieldInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleSlashKey);
    return () => document.removeEventListener('keydown', handleSlashKey);
  }, [field, step]);

  const {
    valueType,
    operators,
    options
  }: {
    valueType: QBFilterValueType;
    operators: QBFilterOptions[];
    options: QBFilterOptions[];
  } = useMemo(() => {
    const fieldOptions = !selectedField
      ? []
      : getFieldOptions(config, selectedField);
    return {
      valueType: selectedFieldConfig?.type || 'str',
      operators: !selectedField
        ? []
        : getOperatorsForField(config, selectedField),
      options: fieldOptions
    };
  }, [selectedFieldConfig, config]);

  useEffect(() => {
    if (options.length > 0 && value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedOptions(
        getSelectedOptions(filter, options).map((opt) => String(opt.value))
      );
    }
  }, [options]);

  useEffect(() => {
    if (!selectedField || selectedFieldConfig) return;

    if (field) {
      onDelete?.(field);
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedField(null);
    setSelectedOperator(null);
    setSelectedValue(null);
    setSelectedOptions([]);
    setStep('field');
  }, [field, onDelete, selectedField, selectedFieldConfig]);

  // this is used to display the label of the selected value(s) in the final step, if there are options available we want to show the label instead of the raw value
  const selectedValueLabel = useMemo(() => {
    if (options.length > 0 && selectedValue) {
      const selectedOpts = getSelectedOptions(filter, options);
      return isEmpty(selectedOpts)
        ? null
        : selectedOpts.map((opt) => opt.label).join(', ');
    }
    return selectedValue;
  }, [selectedValue, options]);

  const handleOnChange = useCallback(
    (fld: string, op: string, val: string) => {
      if (!fld || !op) return;

      // send the new filter changes up to the parent
      const newFilter = {
        field: fld,
        operator: op,
        value: `${val === null ? '' : val}`
      };
      onChange?.(newFilter);

      // clear the default entry field
      if (!field) {
        setSelectedField(null);
        setSelectedOperator(null);
        setSelectedValue(null);
        setStep('field');
      }
    },
    [field, onChange]
  );

  // handler for when a base field is selected from the dropdown
  const handleFieldSelect = useCallback(
    (_event: React.SyntheticEvent, selectedFieldName: string | null) => {
      if (!selectedFieldName) return;

      const vType = config[selectedFieldName]
        ? config[selectedFieldName]?.type || 'str'
        : 'str';
      setSelectedField(selectedFieldName);
      setSelectedValue(null);

      const ops = getOperatorsForField(config, selectedFieldName);

      if (vType === 'datetime' && ops.find((o) => o.value === QBOperator.Gte)) {
        setSelectedOperator(QBOperator.Gte);
      } else {
        setSelectedOperator(ops.length > 0 ? `${ops[0].value}` : null);
      }

      setStep('value');
    },
    [config]
  );

  // handler for free text search when the user types a search term and hits enter
  const handleFieldKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        const target = event.target as HTMLInputElement;
        const searchTerm = target.value;
        const fieldNames = JSON.stringify(Object.keys(config));
        // if search term matches partially a field name do not trigger free search so the field selection dropdown can catch the enter
        if (fieldNames.includes(searchTerm)) {
          return;
        }

        // if there is no match with a field name set the field to search and use the search term as the value
        if (!config[searchTerm]) {
          const newFilter = {
            field: 'search',
            operator: QBOperator.Eq,
            value: searchTerm ?? ''
          };
          onChange?.(newFilter);
          setStep('field');
          fieldInputRef.current?.blur();
        }
      }
    },
    [config, onChange]
  );

  // handler for when an operator is selected from the dropdown
  const handleOperatorSelect = useCallback(
    (selectedOp: string) => {
      setSelectedOperator(selectedOp);

      // For multiselect operators, clear value; otherwise update with new operator
      if (
        isQBInOperator(selectedOp) ||
        (isQBInOperator(selectedOperator ?? '') && !isQBInOperator(selectedOp))
      ) {
        setSelectedValue(null);
        setSelectedOptions([]);
        setStep('value');
      } else {
        setStep('final');
        handleOnChange(selectedField!, selectedOp!, selectedValue as string);
      }
    },
    [selectedField, selectedValue, selectedOperator, handleOnChange]
  );

  // handler for clearing the current filter and resetting the builder to the initial state
  const handleReset = useCallback(() => {
    onDelete?.(field!);
    setSelectedField(null);
    setSelectedOperator(null);
    setSelectedValue(null);
    setSelectedOptions([]);
    setStep('field');
  }, [onDelete, field]);

  // Allow users to hit escape to reset the builder when focused
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleReset();
      }
    },
    [handleReset]
  );

  // this is an internal handler for the searchable select component to manage the selected options state, it does not trigger an onChange to the parent until the user clicks "Apply" or selects an option in single select mode
  const handleOnSelectValueChange = useCallback(
    (key: string, selectedOpt: string[]) => {
      if (selectedOpt.includes(key)) {
        setSelectedOptions(selectedOpt.filter((opt) => opt !== key));
      } else {
        setSelectedOptions([...selectedOpt, key]);
      }
    },
    []
  );

  // handler for when a value is selected from a dropdown or entered in the input, this triggers the onChange to the parent with the new filter values
  const handleOnValueSelect = useCallback(
    (_key: string, selected: string[]) => {
      if (selected.length === 1 && selected[0] === 'Null') {
        setSelectedOperator(QBOperator.IsNull);
        setSelectedValue('true');
        setStep('final');
        handleOnChange(selectedField!, QBOperator.IsNull, 'true');
        return;
      }
      setSelectedOptions(selected);
      setSelectedValue(selected.join(','));
      setStep('final');
      handleOnChange(selectedField!, selectedOperator!, selected.join(','));
    },
    [selectedField, selectedOperator, handleOnChange]
  );

  const handleValueInputChange = useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setSelectedValue(e.target.value === '' ? null : e.target.value);
      setStep('final');
    },
    []
  );

  // value change handler for the free text input, allows users to hit enter to trigger the onChange to the parent with the new filter value
  // otherwise on blur will do the same without requiring the user to hit enter
  const handleValueInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        setSelectedValue((e.target as HTMLInputElement).value);
        setStep('final');
        handleOnChange(
          selectedField!,
          selectedOperator!,
          (e.target as HTMLInputElement).value
        );
      }
    },
    [selectedField, selectedOperator, handleOnChange]
  );

  const isMultiselect = useMemo(
    () => isQBInOperator(selectedOperator ?? ''),
    [selectedOperator]
  );

  const getOptionLabel = useCallback((f: string) => config[f].label, [config]);

  const handleSelectSingleValue = useCallback(
    (v: string | number) => handleOnValueSelect('', [`${v}`]),
    [handleOnValueSelect]
  );

  const handleDateTimeAccept = useCallback(
    (newValue: Dayjs | null) => {
      if (newValue) {
        handleOnValueSelect('', [`${newValue.utc().toISOString()}`]);
      }
    },
    [handleOnValueSelect]
  );

  // needed so the controlled `value` prop reflects in-progress picks, otherwise it snaps back to the old value on every click since only onAccept commits to the parent
  const handleDateTimeChange = useCallback((newValue: Dayjs | null) => {
    setSelectedValue(newValue ? newValue.utc().toISOString() : null);
  }, []);

  const handleOnCancelSelect = useCallback(() => {
    if (selectedValue) {
      setSelectedOptions(
        getSelectedOptions(filter, options).map((opt) => String(opt.value))
      );
      setStep('final');
    }
  }, [selectedValue, filter, options]);

  const rootClassName = useMemo(
    () =>
      step === 'field'
        ? `${classes.queryBuilderRoot} qb-start`
        : classes.queryBuilderRoot,
    [step, classes.queryBuilderRoot]
  );

  const valueStepContent = useMemo(() => {
    if (step !== 'value' || selectedOperator === null) return null;

    switch (valueType) {
      case 'datetime':
        return (
          <DateTimePicker
            className={classes.queryBuilderValueDatetime}
            ampm={false}
            open
            value={
              selectedValue ? dayjs.utc(selectedValue as string).local() : null
            }
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  '& .MuiPickersSectionList-root': {
                    padding: '0.1rem 0rem !important'
                  }
                }
              }
            }}
            onChange={handleDateTimeChange}
            onAccept={handleDateTimeAccept}
          />
        );
      default:
        if (
          options.length > 0 &&
          !isQBContainsOperator(selectedOperator ?? '')
        ) {
          return (
            <SearchableSelect
              title=""
              field="queryBuilder"
              selectedOptions={selectedOptions}
              filterOptions={options}
              showCounts={config[selectedField ?? '']?.showCounts}
              onApply={handleOnValueSelect}
              onChange={handleOnSelectValueChange}
              onSelect={handleSelectSingleValue}
              onCancel={handleOnCancelSelect}
              multiselect={isMultiselect}
              titleButtonProps={{ label: 'Select...' }}
              open
              className={classes.queryBuilderValueSelect}
            />
          );
        }
        return (
          <TextField
            className={`${classes.queryBuilderInput} value-input`}
            size="small"
            placeholder="Enter value"
            value={selectedValue ?? ''}
            autoFocus
            onChange={(e) => setSelectedValue(e.target.value)}
            onKeyDown={handleValueInputKeyDown}
            onBlur={handleValueInputChange}
          />
        );
    }
  }, [
    step,
    selectedOperator,
    valueType,
    selectedValue,
    selectedOptions,
    options,
    isMultiselect,
    classes,
    handleDateTimeAccept,
    handleDateTimeChange,
    handleOnValueSelect,
    handleOnSelectValueChange,
    handleSelectSingleValue,
    handleValueInputKeyDown,
    handleValueInputChange
  ]);

  return (
    <Box className={rootClassName} onKeyDown={handleKeyDown}>
      {step === 'field' && (
        <Autocomplete
          className={classes.queryBuilderFieldAutocomplete}
          size="small"
          options={baseFields}
          getOptionLabel={getOptionLabel}
          onChange={handleFieldSelect}
          onKeyDown={handleFieldKeyDown}
          selectOnFocus
          fullWidth
          clearOnBlur
          openOnFocus
          forcePopupIcon={false}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Search ..."
              inputRef={fieldInputRef}
            />
          )}
          slotProps={{
            popper: { placement: 'bottom-start', sx: { minWidth: '20rem' } }
          }}
        />
      )}

      {selectedFieldConfig && (
        <Typography className={classes.queryBuilderField} component="span">
          {selectedFieldConfig.label}
        </Typography>
      )}

      {(step === 'operator' || selectedOperator !== null) && (
        <Select
          className={`${classes.queryBuilderSelect} operator-select`}
          IconComponent={(props) => (
            <FontAwesomeIcon icon={faAngleDown} {...props} />
          )}
          value={selectedOperator ?? ''}
          displayEmpty
          onChange={(e) => handleOperatorSelect(e.target.value as string)}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
          }}
        >
          {operators.map((op) => (
            <MenuItem
              key={op.value}
              value={op.value}
              sx={{ fontFamily: 'monospace' }}
            >
              {op.label}
            </MenuItem>
          ))}
        </Select>
      )}
      {valueStepContent}

      {step === 'final' &&
        selectedOperator !== QBOperator.IsNull &&
        selectedOperator !== QBOperator.IsNotNull && (
          <QueryBuilderValueDisplay
            valueType={valueType}
            selectedValue={selectedValueLabel ?? selectedValue}
            className={classes.queryBuilderValue}
            onEdit={() => setStep('value')}
          />
        )}

      {selectedField !== null && (
        <FontAwesomeIcon
          icon={faTimesCircle}
          onClick={handleReset}
          className={classes.queryBuilderResetIcon}
        />
      )}
    </Box>
  );
};

export default QueryBuilder;
